// @flow

import React, { useState, type Element } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Container,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Typography,
  Radio,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  useMediaQuery,
} from '@material-ui/core'
import { createStyles, makeStyles, useTheme } from '@material-ui/core/styles'
import { grey } from '@material-ui/core/colors'
import PhoneNumberField from './PhoneNumberField'
import PractitionerNumberField from './PractitionerNumberField'
import FacilityNumberField from './FacilityNumberField'
import HiddenForm from './HiddenForm'
import { core } from '../../../lib/WasmCache'
import urlParam from '../../../utils/urlParam'

import type { Speciality, UrlParams } from '../RegistrationContainer'

const useStyles = makeStyles((theme) =>
  createStyles({
    body1: {
      fontSize: 16,
      color: '#333333',
    },
    formControlLabel: { fontSize: '1.5rem' },
    formControl: {
      width: '100%',
      margin: 0,
    },
    legend: {
      color: '#333333',
      fontSize: 18,
      marginBottom: '16px',
      borderBottom: 0,
    },
    typography: {
      margin: '5px 15px',
    },
    requiredFieldTxt: {
      fontFamily: 'Roboto',
      fontSize: '14px',
      fontWeight: '400',
      lineHeight: '20px',
      textAlign: 'left',
      paddingTop: '15px',
    },
  })
)

type Props = {
  registrationUuid: string,
  activationCode: string,
  allSpecialities: Array<Speciality>,
  selectedCategory: ?string,
  urlParams: UrlParams,
  planBlacklistedForProvince: Array<string>,
  lfpFeatureFlag: boolean,
}

type ValidationResult = {|
  phoneNumber: boolean,
  practitionerNumber: boolean,
  officeFacilityCode: boolean,
|}

function FinalDetails({
  registrationUuid,
  activationCode,
  allSpecialities,
  selectedCategory,
  urlParams,
  planBlacklistedForProvince,
  lfpFeatureFlag,
}: Props): Element<'div'> {
  const formId: string = 'new_user_registration'
  const classes = useStyles()
  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.down('xs'))

  const [province, setProvince] = useState(urlParam('province'))
  const [category, setCategory] = useState(selectedCategory ?? '')
  const [specialityId, setSpeciality] = useState('')
  const [practitionerNumber, setPractitionerNumber] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [lfpEnrolled, setLfpEnrolled] = useState(false)
  const [officeFacilityCode, setOfficeFacilityCode] = useState('')
  const [validationResults, setValidationResults] = useState<ValidationResult>({
    phoneNumber: false,
    practitionerNumber: false,
    officeFacilityCode: false,
  })

  const handleProvinceChange = (event: SyntheticInputEvent<HTMLElement>): void => {
    setProvince(event.target.value)

    setSpeciality('')
    setCategory('')
    setPractitionerNumber('')
    setLfpEnrolled(false)
  }

  const handleCategoryChange = (event: SyntheticInputEvent<HTMLElement>): void => {
    setCategory(event.target.value)
  }

  const handleSpecialityChange = (event: SyntheticInputEvent<HTMLElement>): void => {
    setSpeciality(event.target.value)
  }

  const handleLfpEnrollmentChange = (event: SyntheticInputEvent<HTMLElement>): void => {
    setLfpEnrolled(event.target.checked)

    setOfficeFacilityCode('')
  }

  function onEssentialsPlanSelected(): boolean {
    const blacklistedProvinceCode = core
      .supportedProvinces()
      .find((p) => p.name === 'Ontario')?.code
    return (
      province === blacklistedProvinceCode &&
      planBlacklistedForProvince.includes(blacklistedProvinceCode)
    )
  }

  function updateValidationResult(fieldName: $Keys<ValidationResult>, isValid: boolean) {
    setValidationResults((res) => ({ ...res, ...{ [(fieldName: string)]: isValid } }))
  }

  function isFacilityNumberValid(): boolean {
    if (!lfpEnrolled) {
      return true
    }

    return validationResults.officeFacilityCode
  }

  function isFormValid(): boolean {
    return (
      province !== '' &&
      category !== '' &&
      specialityId !== '' &&
      validationResults.practitionerNumber &&
      validationResults.phoneNumber &&
      isFacilityNumberValid()
    )
  }

  function renderMessageForNewPhysician(): Element<typeof Grid> | null {
    return category === 'new_physician' ? (
      <Grid item xs={12}>
        <Typography variant="h6">Don’t have a practitioner number yet?</Typography>
        <Typography>Leave this field blank for now, and get in touch once you have it.</Typography>
      </Grid>
    ) : null
  }

  const handleGoToDashboard = (event: SyntheticInputEvent<HTMLElement>): void => {
    const form: ?HTMLElement = document.getElementById(formId)
    if (form instanceof HTMLFormElement) {
      form.submit()
    } else {
      const error = new Error('Unable to find formId new_user_registration in the sign up page')
      window.newrelic.noticeError(error)
    }
  }

  function renderMessageToJoinThroughMdbilling(): Element<typeof Grid> | null {
    return onEssentialsPlanSelected() ? (
      <Grid item xs={12}>
        <Typography style={theme.typography.default}>
          If you are interested in our Essentials plan in Ontario, please create your account{' '}
          <a href="https://secure.mdbilling.ca/NewSignUp/GetSignUp">here</a>.
        </Typography>
      </Grid>
    ) : null
  }

  const specialities = allSpecialities.filter((s) => s['province'] === province)
  const noProvinceSelected = province === '' || onEssentialsPlanSelected()
  const supportFieldDisplay = noProvinceSelected === true ? 'none' : 'block'
  const supportLfpDisplay = lfpFeatureFlag && province === 'BC' ? 'block' : 'none'
  const supportFacilityNumberDisplay = province === 'BC' && lfpEnrolled ? 'block' : 'none'

  return (
    <Container maxWidth="sm">
      <HiddenForm
        formData={{
          formId,
          registrationUuid,
          activationCode,
          province,
          category,
          specialityId,
          practitionerNumber,
          phoneNumber,
          lfpEnrolled,
          officeFacilityCode,
          urlParams,
        }}
      />
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h3">About your Practice</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography style={theme.typography.default}>
            Let us know a bit more about your practice to finish creating your account.
          </Typography>
          <Typography className={classes.requiredFieldTxt}>* indicates required field</Typography>
        </Grid>
        <Grid item xs={12}>
          <FormControl component="fieldset" required>
            <FormLabel className={classes.legend} component="legend">
              Which province do you bill to?
            </FormLabel>
            <RadioGroup
              aria-label="Which province do you bill to?"
              value={province}
              onChange={handleProvinceChange}
            >
              {core.supportedProvinces().map((province) => (
                <FormControlLabel
                  key={province.id}
                  name={`province-${province.code}`}
                  value={province.code}
                  control={<Radio />}
                  label={`${province.name} (${province.health_care_system})`}
                  required
                  className={classes.body1}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid
          component={Box}
          className={classes.gridItem}
          item
          xs={12}
          display={supportLfpDisplay}
          data-testid="registration-step-3-lfp-enrollment"
          style={{ marginTop: 30 }}
        >
          <FormLabel className={classes.legend} component="legend">
            Patient panel upload (PAS)
          </FormLabel>
          <FormControlLabel
            control={
              <Checkbox
                checked={lfpEnrolled}
                onChange={handleLfpEnrollmentChange}
                color="primary"
                data-testid="lfp_checkbox"
              />
            }
            label={
              <Box color={grey[900]}>
                <Typography component="span" variant="body2">
                  <Box display="inline">I am signing up for Dr.Bill to upload my </Box>
                  <Box display="inline" fontWeight="bold">
                    {' '}
                    patient panel{' '}
                  </Box>
                  <Box display="inline"> to MSP.</Box>
                </Typography>
              </Box>
            }
          />
        </Grid>
        <Grid
          component={Box}
          className={classes.gridItem}
          item
          xs={12}
          display={supportFieldDisplay}
          data-testid="registration-step-3-category"
          style={{ marginTop: 30 }}
        >
          <FormLabel className={classes.legend} component="legend">
            Final Details
          </FormLabel>
          <FormControl variant="outlined" className={classes.formControl} required>
            <InputLabel id="category-label">What best describes you</InputLabel>
            <Select
              labelId="category-label"
              id="category"
              value={category}
              onChange={handleCategoryChange}
              label="What best describes you"
            >
              {core.signUpCategories().map((category) => (
                <MenuItem key={category.id} value={category.code}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          component={Box}
          className={classes.gridItem}
          item
          xs={12}
          display={supportFieldDisplay}
          data-testid="registration-step-3-specialty"
        >
          <FormControl
            variant="outlined"
            className={classes.formControl}
            required
            disabled={noProvinceSelected}
          >
            <InputLabel id="speciality-label">Your speciality</InputLabel>
            <Select
              labelId="speciality-label"
              id="speciality"
              value={specialityId}
              onChange={handleSpecialityChange}
              label="Your speciality"
            >
              {specialities.map((s) => {
                return (
                  <MenuItem key={s.id} value={s.id}>
                    {s.title}
                  </MenuItem>
                )
              })}
            </Select>
            <FormHelperText>Please select what best describes you *</FormHelperText>
            {noProvinceSelected && (
              <FormHelperText>Please select a province to bill</FormHelperText>
            )}
          </FormControl>
        </Grid>
        {renderMessageForNewPhysician()}
        {renderMessageToJoinThroughMdbilling()}
        <Grid
          component={Box}
          className={classes.gridItem}
          item
          xs={12}
          display={supportFieldDisplay}
          data-testid="registration-step-3-practitioner-number"
        >
          <PractitionerNumberField
            practitionerNumber={practitionerNumber}
            province={province}
            category={category}
            isLfpEnrolled={lfpEnrolled}
            setPractitionerNumber={setPractitionerNumber}
            setIsValid={(isValid) => updateValidationResult('practitionerNumber', isValid)}
          />
        </Grid>
        <Grid
          component={Box}
          className={classes.gridItem}
          item
          xs={12}
          display={supportFieldDisplay}
          data-testid="registration-step-3-phone-number"
        >
          <PhoneNumberField
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            setIsValid={(isValid) => updateValidationResult('phoneNumber', isValid)}
          />
        </Grid>
        <Grid
          component={Box}
          className={classes.gridItem}
          item
          xs={12}
          display={supportFacilityNumberDisplay}
          data-testid="registration-step-3-facility-number"
        >
          <FacilityNumberField
            value={officeFacilityCode}
            setValue={setOfficeFacilityCode}
            setIsValid={(isValid) => updateValidationResult('officeFacilityCode', isValid)}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            style={{ float: 'right' }}
            fullWidth={matches}
            variant="contained"
            color="primary"
            type="submit"
            disabled={!isFormValid()}
            onClick={handleGoToDashboard}
            form={formId}
          >
            {lfpEnrolled ? 'Go to patient panel page' : 'Go to dashboard'}
          </Button>
        </Grid>
      </Grid>
    </Container>
  )
}

export default FinalDetails
